import { supabase } from '../../../lib/supabase';
import { contactPhoneKeyVariants } from '../../../utils/phoneUtils';
import { isTemporaryRemoteMediaUrl } from '../../../utils/failedImageCache';
import { buildStageNameMap, buildLeadStageMap } from './PhoneStageMapper';

export interface EnrichmentMaps {
    contactIdMap: Map<string, string>;
    contactNameMap: Map<string, string>;
    contactAvatarMap: Map<string, string>;
    leadStageMap: Map<string, string>;
}

// --- Cache em memória ---
const CACHE_TTL_MS = 30_000; // 30 segundos

interface CacheEntry {
    data: EnrichmentMaps;
    timestamp: number;
}

const enrichmentCache = new Map<string, CacheEntry>();

function getCachedMaps(orgId: string): EnrichmentMaps | null {
    const entry = enrichmentCache.get(orgId);
    if (!entry) return null;
    
    const age = Date.now() - entry.timestamp;
    if (age > CACHE_TTL_MS) {
        enrichmentCache.delete(orgId);
        return null;
    }
    
    return entry.data;
}

function setCachedMaps(orgId: string, data: EnrichmentMaps): void {
    enrichmentCache.set(orgId, {
        data,
        timestamp: Date.now()
    });
}

/**
 * Invalida o cache de enrichment para uma organização.
 * Deve ser chamado após sync de chats/contatos para garantir que as negociações ficam vinculadas.
 */
export function invalidateEnrichmentCache(orgId?: string): void {
    if (orgId) {
        enrichmentCache.delete(orgId);
    } else {
        enrichmentCache.clear();
    }
}

/**
 * Busca dados de contatos, estágios e leads para enriquecer conversas
 * 
 * @param organizationId - ID da organização
 * @param forceRefresh - Se true, ignora o cache e busca dados frescos
 * @returns Mapas para lookup de nome de contato e estágio por telefone
 */
export async function fetchEnrichmentMaps(
    organizationId: string,
    forceRefresh = false
): Promise<EnrichmentMaps> {
    // Verifica cache primeiro
    if (!forceRefresh) {
        const cached = getCachedMaps(organizationId);
        if (cached) return cached;
    }

    // 1. Busca contatos da organização
    const { data: savedContacts } = await supabase
        .from('contacts')
        .select('id, name, phone, whatsapp, profile_picture_url')
        .eq('organization_id', organizationId);

    const contactIdMap = new Map<string, string>();
    const contactNameMap = new Map<string, string>();
    const contactAvatarMap = new Map<string, string>();

    const registerPhoneKeys = <T>(map: Map<string, T>, rawPhone: string | null | undefined, value: T) => {
        contactPhoneKeyVariants(rawPhone).forEach((key) => map.set(key, value));
    };
    
    savedContacts?.forEach((c: any) => {
        if (c.phone) {
            registerPhoneKeys(contactIdMap, c.phone, c.id);
            registerPhoneKeys(contactNameMap, c.phone, c.name);
            if (c.profile_picture_url && !isTemporaryRemoteMediaUrl(c.profile_picture_url)) {
                registerPhoneKeys(contactAvatarMap, c.phone, c.profile_picture_url);
            }
        }
        if (c.whatsapp) {
            registerPhoneKeys(contactIdMap, c.whatsapp, c.id);
            registerPhoneKeys(contactNameMap, c.whatsapp, c.name);
            if (c.profile_picture_url && !isTemporaryRemoteMediaUrl(c.profile_picture_url)) {
                registerPhoneKeys(contactAvatarMap, c.whatsapp, c.profile_picture_url);
            }
        }
    });

    // 2. Busca estágios do funil
    const { data: allStages } = await supabase
        .from('funnel_stages')
        .select('id, name');

    const stageNameMap = buildStageNameMap(allStages || []);

    // 3. Busca oportunidades ativas
    const { data: leads } = await supabase
        .from('opportunities')
        .select('id, stage, contact:contacts(phone, whatsapp)')
        .eq('organization_id', organizationId)
        .is('won_at', null)
        .is('lost_at', null)
        .order('created_at', { ascending: false })
        .limit(500);

    const leadStageMap = buildLeadStageMap(leads || [], stageNameMap);

    const result = { contactIdMap, contactNameMap, contactAvatarMap, leadStageMap };
    
    // Salva no cache
    setCachedMaps(organizationId, result);

    return result;
}

